import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Theme } from "@mui/material/styles";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AgentPanel, ChatPanel } from "llmasaservice-ui";
import { API } from "aws-amplify";
import { CircularProgress } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Conversation from "../enterprise/conversation";
import { useLLM } from "llmasaservice-client";

import {
  setCookie,
  getCookie,
  COOKIE_TOKEN,
  COOKIE_EMAIL,
  isProduction,
  removeCookie,
} from "../enterprise/tokenmanagement";

const useStyles = makeStyles()((theme: Theme) => ({
  list: {
    width: 250,
  },

  listContainer: {
    overflowY: "auto",
    paddingBottom: "25px",
  },

  conversationItem: {
    color: theme.palette.text.primary,
  },

  activeConversationItem: {
    backgroundColor: "#555555",
    color: theme.palette.primary.contrastText,
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.contrastText,
    },
  },
}));

const EnterpriseChat: React.FC = () => {
  const { classes } = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // state for authentication
  const [staySignedInDays, setStaySignedInDays] = useState<number>(7);
  const [customerId, setCustomerId] = React.useState<string | null>(null);
  const [token, setToken] = React.useState<string | null>(null);
  const [enterpriseData, setEnterpriseData] = React.useState<any | null>(null);
  const [email, setEmail] = React.useState<string | null>(null);
  const [signInRequestResponse, setSignInRequestResponse] = React.useState<
    any | null
  >(null);
  const [otp, setOtp] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [otpDigits, setOtpDigits] = React.useState<string[]>(Array(6).fill(""));
  const otpInputRefs = Array(6)
    .fill(0)
    .map(() => React.createRef<HTMLInputElement>());
  const [tokenRefreshRetryCount, setTokenRefreshRetryCount] = useState(0);
  const [isAutoLoginInProgress, setIsAutoLoginInProgress] =
    React.useState(false);

  // state for chat
  const [initialized, setInitialized] = React.useState(false);
  const [agents, setAgents] = React.useState<any[]>([]);
  const [conversationList, setConversationList] = React.useState<any[]>([]);
  const [conversation, setConversation] = React.useState<Conversation | null>(
    null
  );
  const [isConversationLoading, setIsConversationLoading] =
    React.useState(false);
  const [menuConversation, setMenuConversation] =
    React.useState<Conversation | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [conversationMenuAnchorEl, setConversationMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [newConversationName, setNewConversationName] = React.useState("");
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [showDeletedConversations, setShowDeletedConversations] =
    useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);

  //--------------------------------------------------------------------------------
  // Theme helpers
  //--------------------------------------------------------------------------------
  const THEME_KEY = "_llmasaservice_theme";

  const toggleTheme = () => {
    const ls = getTheme();
    localStorage.setItem(THEME_KEY, ls === "light" ? "dark" : "light");
    window.location.reload();
  };

  const getTheme = () => {
    const storedTheme = localStorage.getItem(THEME_KEY);
    if (!storedTheme) {
      localStorage.setItem(THEME_KEY, "dark");
      return "dark";
    }
    return storedTheme;
  };

  //--------------------------------------------------------------------------------
  // Token Authentication
  //--------------------------------------------------------------------------------
  const tokenRefreshTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (initialized || !id) return;

    const initialize = async () => {
      const sessionRecovered = recoverSessionData();

      if (sessionRecovered) {
        resetRefreshTimer();
      } else {
        // If session recovery failed, continue with URL-based login attempt
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const urlemail = urlParams.get("email");

        if (code && urlemail) {
          const decodedEmail = decodeURIComponent(urlemail);
          setEmail(decodedEmail);
          setOtp(code);
          setOtpDigits(code.split("").slice(0, 6));

          setIsAutoLoginInProgress(true);
          const loginSuccess = await handleUrlBasedLogin(code, decodedEmail);
          setIsAutoLoginInProgress(false);

          if (loginSuccess) {
            resetRefreshTimer();
          }
        }
      }
    };

    initialize();
    setInitialized(true);

    return () => {
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
    };
  }, [initialized, id]);

  // Add this effect to reset the timer when token changes
  useEffect(() => {
    if (token) {
      console.log("Token changed, resetting refresh timer");
      resetRefreshTimer();
      fetchAgents();
      fetchConversations();
    } else {
      // Clear the timer if token is cleared
      if (tokenRefreshTimerRef.current) {
        console.log("Token cleared, stopping refresh timer");
        clearInterval(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
    }
  }, [token]);

  const recoverSessionData = () => {
    try {
      // Try cookies first
      const cookieToken = getCookie(COOKIE_TOKEN);
      const cookieEmail = getCookie(COOKIE_EMAIL);

      if (cookieToken && cookieEmail) {
        console.log("Session recovered from cookies");
        setToken(cookieToken);
        setEmail(cookieEmail);
        setCustomerId(cookieEmail);

        // Always trigger refresh check on recovery
        setTimeout(() => {
          checkAndRefreshToken();
        }, 0);

        return true;
      }

      // Fall back to localStorage if cookies aren't available
      const sessionData = localStorage.getItem(
        "llmSessionData" + (isProduction ? "_master" : "_dev")
      );

      if (sessionData) {
        console.log("sessionData", sessionData);

        const { token, email, timestamp, projectId, expiresInDays } =
          JSON.parse(sessionData);

        // Calculate how long the token is supposed to be valid for based on stored preferences
        const maxAgeMs = (expiresInDays || 7) * 24 * 60 * 60 * 1000;

        // Only restore if it's for the same project and within the original expiry window
        if (projectId === id && Date.now() - timestamp < maxAgeMs) {
          console.log(
            "Session recovered from localStorage, token age:",
            (Date.now() - timestamp) / (1000 * 60 * 60),
            "hours"
          );

          // Set session data regardless of token expiration - let checkAndRefreshToken handle that
          setToken(token);
          setEmail(email);
          setCustomerId(email);

          // Re-establish cookies from localStorage
          setCookie(COOKIE_TOKEN, token, expiresInDays || 7);
          setCookie(COOKIE_EMAIL, email, expiresInDays || 7);

          // Always trigger refresh check on recovery, just like with cookies
          setTimeout(() => {
            checkAndRefreshToken();
          }, 0);

          return true;
        } else {
          console.log(
            "localStorage session data expired or for different project, removing"
          );
          localStorage.removeItem(
            "llmSessionData" + (isProduction ? "_master" : "_dev")
          );
        }
      }

      return false;
    } catch (error) {
      console.error("Error recovering session data:", error);
      return false;
    }
  };

  const checkAndRefreshToken = async () => {
    console.log(
      "Checking token and refreshing if needed...",
      new Date().toISOString()
    );

    const currentToken = getCookie(COOKIE_TOKEN);
    const storedEmail = getCookie(COOKIE_EMAIL);

    if (!currentToken || !storedEmail || !id) {
      console.log("Missing required data for token refresh");
      return false;
    }

    // Check if token is expired or expiring soon
    const isExpired = isTokenExpired(currentToken);
    const isExpiringSoon = isTokenExpiringSoon(currentToken, 20);

    console.log(
      "Token status - Expired:",
      isExpired,
      "Expiring soon:",
      isExpiringSoon
    );

    if (!isExpired && !isExpiringSoon) {
      console.log("Token still valid, no need to refresh yet");
      return true;
    }

    console.log(
      isExpired
        ? "Token is expired, refreshing"
        : "Token expiring soon, refreshing"
    );
    const result = await doRefreshToken(storedEmail, currentToken);
    return result;
  };

  const isTokenExpiringSoon = (token: string, minutesBuffer = 10): boolean => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const bufferTime = minutesBuffer * 60 * 1000; // Convert minutes to milliseconds

      // For debugging, you could log:
      console.log(`Token expires at: ${new Date(expiryTime).toISOString()}`);
      console.log(`Current time: ${new Date(currentTime).toISOString()}`);
      console.log(
        `Buffer threshold: ${new Date(expiryTime - bufferTime).toISOString()}`
      );
      console.log(
        `Time until expiry: ${(expiryTime - currentTime) / 1000 / 60} minutes`
      );

      // True if current time is within buffer window of expiry
      return currentTime >= expiryTime - bufferTime;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true; // Assume expired if we can't verify
    }
  };

  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiryTime;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true; // Assume expired if we can't verify
    }
  };

  const doRefreshToken = async (storedEmail: string, currentToken: string) => {
    try {
      console.log("Refreshing token...");
      setIsLoading(true);

      const isExpired = isTokenExpired(currentToken);
      console.log("Token expired status:", isExpired);

      console.log("Calling API to refresh token...");
      console.log("Token:", currentToken);
      console.log("Email:", storedEmail);
      console.log("ID:", id);

      const response = await API.post("eventapi", "/enterpriseRefreshToken", {
        body: {
          id: id,
          email: storedEmail,
          token: currentToken,
        },
      });

      console.log("Token refresh response:", response);

      if (response?.data) {
        const data = JSON.parse(response.data);
        if (data.token) {
          console.log("Token refreshed successfully");
          // Store the new token
          setCookie(COOKIE_TOKEN, data.token, staySignedInDays);
          setToken(data.token);

          // Update localStorage if we're using it as backup
          const sessionData = localStorage.getItem(
            "llmSessionData" + (isProduction ? "_master" : "_dev")
          );
          if (sessionData) {
            const parsed = JSON.parse(sessionData);
            localStorage.setItem(
              "llmSessionData" + (isProduction ? "_master" : "_dev"),
              JSON.stringify({
                ...parsed,
                token: data.token,
                timestamp: Date.now(),
              })
            );
          }

          // Reset retry count on success
          setTokenRefreshRetryCount(0);

          // Reset the timer to check again in 30 minutes
          resetRefreshTimer();
          return true;
        }
      }

      console.error("Token refresh failed - no token in response");
      handleRetryOrSignout();
      return false;
    } catch (error) {
      console.error("Error refreshing token:", error);
      handleRetryOrSignout();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryOrSignout = () => {
    const MAX_RETRIES = 3; // Maximum number of allowed retries

    // Increment retry count
    const newRetryCount = tokenRefreshRetryCount + 1;
    setTokenRefreshRetryCount(newRetryCount);

    if (newRetryCount < MAX_RETRIES) {
      // Still within retry limit, schedule another attempt
      console.log(
        `Token refresh failed. Attempt ${newRetryCount}/${MAX_RETRIES}. Retrying in 5 minutes...`
      );

      // Clear any existing timer
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }

      // Schedule retry
      tokenRefreshTimerRef.current = setTimeout(
        checkAndRefreshToken,
        7 * 60 * 1000 // 5 minutes
      );
    } else {
      // Exceeded retry limit, sign out the user
      console.log(
        `Token refresh failed after ${MAX_RETRIES} attempts. Signing out...`
      );

      // Show a notification to the user that they're being signed out
      setErrorMessage(
        "Your session has expired after multiple failed refresh attempts. Please sign in again."
      );

      // Sign out after a brief delay to allow the user to see the message
      setTimeout(() => {
        handleSignout();
      }, 3000);
    }
  };

  const resetRefreshTimer = () => {
    if (tokenRefreshTimerRef.current) {
      console.log("Clearing existing token refresh timer");
      clearInterval(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }

    const refreshInterval = 15 * 60 * 1000;

    console.log(
      `Setting new token refresh timer to run every ${
        refreshInterval / 1000
      } seconds`
    );

    const refreshFunction = async () => {
      console.log(`Timer triggered at ${new Date().toISOString()}`);
      await checkAndRefreshToken();
    };

    tokenRefreshTimerRef.current = setInterval(
      refreshFunction,
      refreshInterval
    );

    // Run the check immediately - this ensures we don't have to wait for the first interval
    refreshFunction();

    console.log("Token refresh timer reset.");
  };

  const handleSignout = () => {
    setCustomerId(null);
    setToken(null);
    setEmail(null);
    removeCookie(COOKIE_EMAIL);
    removeCookie(COOKIE_TOKEN);

    // Reset OTP-related state
    setSignInRequestResponse(null);
    setOtp(null);
    setOtpDigits(Array(6).fill(""));
    setErrorMessage(null);

    if (tokenRefreshTimerRef.current) {
      clearInterval(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }

    // Clear localStorage if needed
    localStorage.removeItem(
      "llmSessionData" + (isProduction ? "_master" : "_dev")
    );
  };

  const handleUrlBasedLogin = async (code: string, emailAddress: string) => {
    try {
      const response = await API.post(
        "eventapi",
        "/enterprisePasswordlessLoginConfirmCode",
        {
          body: {
            id: id,
            email: emailAddress,
            code: code,
          },
          credentials: "include",
        }
      );

      if (response && response.data) {
        const data = JSON.parse(response.data);
        if (data.codeValid && data.codeValid === true) {
          console.log("OTP confirmed from URL. Logging in.");
          setCookie(COOKIE_TOKEN, data.token ?? "", staySignedInDays);
          setCookie(COOKIE_EMAIL, emailAddress ?? "", staySignedInDays);
          setCustomerId(emailAddress);
          setToken(data.token);

          // Clear the URL parameters after successful login
          navigate(location.pathname, { replace: true });

          return true;
        }
      }
      setErrorMessage(
        "Invalid verification code from link. Please try requesting a new code."
      );
      return false;
    } catch (error) {
      console.error("Error with automatic login from URL:", error);
      setErrorMessage(
        "Failed to verify the code from the link. Please try requesting a new code."
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (
    email: string | null
  ): { valid: boolean; errorMessage?: string } => {
    if (!email) {
      return { valid: false, errorMessage: "Email address is required" };
    }

    // Basic email format validation
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      return { valid: false, errorMessage: "Invalid email format" };
    }

    // Domain whitelist validation if we have enterprise data
    if (enterpriseData?.domainWhitelist) {
      const domain = email.split("@")[1].toLowerCase();
      const allowedDomains = enterpriseData.domainWhitelist
        .split(",")
        .map((d: string) => d.trim().toLowerCase());

      if (!allowedDomains.includes(domain)) {
        return {
          valid: false,
          errorMessage: `Only email addresses from the following domains are allowed: ${enterpriseData.domainWhitelist}`,
        };
      }
    }

    return { valid: true };
  };

  const handleSignInClick = async () => {
    if (!id || !email) return;

    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setErrorMessage(emailValidation.errorMessage || null);
      return;
    }

    setErrorMessage(null);
    try {
      const response = await API.post(
        "eventapi",
        "/enterprisePasswordlessLogin",
        {
          body: {
            id: id,
            email: email,
          },
        }
      );

      if (response && response.data) {
        setSignInRequestResponse(JSON.parse(response.data));
      }
    } catch (error: any) {
      // Check for specific error responses from the server
      const serverError = error?.response?.data?.error;
      if (serverError) {
        setErrorMessage(serverError);
      } else {
        setErrorMessage("Failed to send verification code. Please try again.");
      }
      console.error("Error sending login email:", error);
    }
  };

  const handleOtpSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await API.post(
        "eventapi",
        "/enterprisePasswordlessLoginConfirmCode",
        {
          body: {
            id: id,
            email: email,
            code: otp,
          },
        }
      );

      if (response && response.data) {
        const data = JSON.parse(response.data);
        if (data.codeValid && data.codeValid === true) {
          console.log("OTP confirmed. Logging in.");
          setCookie(COOKIE_TOKEN, data.token ?? "", staySignedInDays);
          setCookie(COOKIE_EMAIL, email ?? "", staySignedInDays);
          setCustomerId(email);
          setToken(data.token);
        } else {
          setErrorMessage("Invalid OTP. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error logging in:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderOtpInput = () => (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between">
        {otpDigits.map((digit, index) => (
          <TextField
            key={index}
            inputRef={otpInputRefs[index]}
            value={digit}
            variant="outlined"
            size="small"
            inputProps={{
              maxLength: 1,
              style: { textAlign: "center", width: "2rem" },
            }}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue && !/^\d+$/.test(newValue)) return;

              // Update the OTP digits
              const newOtpDigits = [...otpDigits];
              newOtpDigits[index] = newValue;
              setOtpDigits(newOtpDigits);
              setOtp(newOtpDigits.join(""));

              // Auto-focus next input
              if (newValue && index < 5) {
                otpInputRefs[index + 1].current?.focus();
              } else if (newValue && index === 5) {
                // Auto-submit when all digits are filled
                const allFilled = newOtpDigits.every((digit) => digit !== "");
                if (allFilled) {
                  handleOtpSubmit();
                }
              }
            }}
            onKeyDown={(e) => {
              // Handle backspace
              if (e.key === "Backspace" && !digit && index > 0) {
                otpInputRefs[index - 1].current?.focus();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pastedData = e.clipboardData.getData("text");
              if (!/^\d+$/.test(pastedData)) return;

              const pastedOtp = pastedData.slice(0, 6).split("");
              const newOtpDigits = [...otpDigits];
              pastedOtp.forEach((char, i) => {
                if (i < 6) newOtpDigits[i] = char;
              });

              setOtpDigits(newOtpDigits);
              setOtp(newOtpDigits.join(""));

              // Focus the next empty field or the last field
              const nextEmptyIndex = newOtpDigits.findIndex((d) => !d);
              if (nextEmptyIndex !== -1) {
                otpInputRefs[nextEmptyIndex].current?.focus();
              } else {
                otpInputRefs[5].current?.focus();
              }
            }}
          />
        ))}
      </Box>
      <Button
        variant="contained"
        onClick={handleOtpSubmit}
        disabled={isLoading || !otp || otp.length !== 6}
      >
        {isLoading ? "Verifying..." : "Verify Code"}
      </Button>
      {errorMessage && (
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      )}
      <Typography variant="caption">
        Didn't receive a code?{" "}
        <Button variant="text" size="small" onClick={handleSignInClick}>
          Send again
        </Button>
      </Typography>
    </Stack>
  );

  //--------------------------------------------------------------------------------
  // Setting up the Agents and Chat
  //--------------------------------------------------------------------------------

  const handleAgentSelect = async (agent: any) => {
    setSelectedAgent(agent);
    setAnchorEl(null);

    const newConversation = new Conversation(agent?.id || null);

    try {
      const response = await API.post("eventapi", "/newConversation", {
        body: {
          id: newConversation.id,
          title: newConversation.title,
          token: token,
          agentId: agent?.id || null,
        },
      });

      if (response && response.data) {
        const data = JSON.parse(response.data);
      } else {
        console.error("Error creating conversation:", error);
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
    }

    setConversation(newConversation);
    setConversationList((current) => [...current, newConversation]);
  };

  useEffect(() => {
    if (!id) return;

    const fetchEnterpriseLoginDetails = async () => {
      try {
        const response = await API.post("eventapi", "/enterpriseLoginDetails", {
          body: {
            id: id,
          },
        });

        if (response && response.data) {
          setEnterpriseData(JSON.parse(response.data));
        }
      } catch (error) {
        console.error("Error fetching enterprise login details:", error);
      }
    };

    if (id) {
      fetchEnterpriseLoginDetails();
    }
  }, [id]);

  useEffect(() => {
    if (token) {
      fetchAgents();
      fetchConversations();
    }
  }, [token]);

  const fetchAgents = async () => {
    try {
      const result = await API.get("eventapi", "/agents", {
        queryStringParameters: {
          token: token,
        },
        credentials: "include",
      });

      if (result && result.data) {
        setAgents(await JSON.parse(result.data));
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const result = await API.get("eventapi", "/conversations", {
        queryStringParameters: {
          token: token,
        },
      });

      if (result && result.data) {
        const convoData = JSON.parse(result.data);

        const list = convoData.map((entry: any) => {
          const conversation = new Conversation(entry.agentId);
          conversation.id = entry.id;
          conversation.title = entry.title;
          conversation.createdAt = new Date(entry.createdAt);
          conversation.updatedAt = new Date(entry.updatedAt);
          conversation.status = entry.status;

          return conversation;
        });

        setConversationList(list);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const getAgentName = (agentId: string) => {
    const agent = agents.find((a) => a.id === agentId);
    return agent ? agent.name : "No agent";
  };

  const setConversationClick = async (c: Conversation) => {
    setIsConversationLoading(true);
    if (c.agentId && c.agentId !== "") {
      const agent = agents.find((agent) => agent.id === c.agentId);

      if (agent) {
        setSelectedAgent(agent);
      }
    } else {
      setSelectedAgent(undefined);
    }

    try {
      await c.load(token);
      setConversation(c);
    } catch (error) {
      console.error("Error loading conversation:", error);
    } finally {
      setTimeout(() => setIsConversationLoading(false), 500);
    }
  };

  const renderConversationItem = (c: Conversation) => (
    <ListItem
      key={c.id}
      className={
        conversation && c.id === conversation.id
          ? classes.activeConversationItem
          : classes.conversationItem
      }
    >
      <ListItemButton
        onClick={async () => {
          setConversationClick(c);
        }}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <ListItemText
          primary={c.title}
          secondary={
            <Typography variant="caption">
              {getAgentName(c.agentId ?? "No agent")}
            </Typography>
          }
        />
      </ListItemButton>
      <IconButton
        onClick={(e) => handleConversationMenuOpen(e, c)}
        size="small"
        sx={{
          padding: "2px",
          marginLeft: "-8px",
          "& .MuiSvgIcon-root": {
            fontSize: "1rem",
          },
        }}
      >
        <MoreHorizIcon />
      </IconButton>
    </ListItem>
  );

  //--------------------------------------------------------------------------------
  // Conversation Menu Handlers
  //--------------------------------------------------------------------------------
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConversationMenuOpen = async (
    event: React.MouseEvent<HTMLButtonElement>,
    c: Conversation
  ) => {
    setMenuConversation(c);
    setConversationMenuAnchorEl(event.currentTarget);
  };

  const handleConversationMenuClose = () => {
    setConversationMenuAnchorEl(null);
  };

  const handleRenameClose = () => {
    setRenameOpen(false);
  };

  const handleRenameConversation = () => {
    if (menuConversation) {
      setRenameOpen(true);
      setNewConversationName(menuConversation.title);

      if (conversationMenuAnchorEl) {
        const rect = conversationMenuAnchorEl.getBoundingClientRect();
        setModalPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
    }

    handleConversationMenuClose();
  };

  const handleRenameSubmit = async () => {
    if (menuConversation && newConversationName !== "") {
      try {
        const response = await API.post("eventapi", "/updateConversation", {
          body: {
            id: menuConversation.id,
            title: newConversationName,
            token: token,
          },
        });

        if (response && response.data) {
          const data = JSON.parse(response.data);
          menuConversation.updateTitle(newConversationName);
          setConversationList((current) => [...current]);
        }
      } catch (error) {
        console.error("Error renaming conversation:", error);
      } finally {
        setRenameOpen(false);
      }
    }
  };

  const handleDeleteConversation = async () => {
    if (menuConversation) {
      try {
        const response = await API.post("eventapi", "/updateConversation", {
          body: {
            id: menuConversation.id,
            status: menuConversation.status === "active" ? "deleted" : "active",
            token: token,
          },
        });

        if (response && response.data) {
          const data = JSON.parse(response.data);
          menuConversation.status =
            menuConversation.status === "active" ? "deleted" : "active";
          setConversationList((current) => [...current]);
          if (!showDeletedConversations) setConversation(null);
        }
      } catch (error) {
        console.error("Error deleting/restoring conversation:", error);
      }
    }

    handleConversationMenuClose();
  };

  //--------------------------------------------------------------------------------
  // LLM Chat helpers and hooks
  //--------------------------------------------------------------------------------
  // get the right chat url so we can test in dev
  let url = "https://chat.llmasaservice.io/";
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }
  // useLLM hook for suggestions
  const { send, response, idle, error } = useLLM({
    project_id: id ?? "",
    url: url,
  });

  //--------------------------------------------------------------------------------
  // Render
  //--------------------------------------------------------------------------------
  return (
    <>
      {!token && isAutoLoginInProgress && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(255,255,255,0.7)"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          zIndex={9999}
        >
          <CircularProgress size={40} sx={{ marginBottom: 2 }} />
          <Typography variant="h6">Verifying your login link...</Typography>
        </Box>
      )}

      {!token && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <Box
            border={1}
            borderColor="grey.500"
            borderRadius={2}
            padding={4}
            maxWidth="md"
            width="100%"
          >
            <Stack spacing={2}>
              <Typography variant="h5">
                {`Welcome to the LLM Chat Service for ${
                  enterpriseData?.name ?? ""
                }`}
              </Typography>
              <TextField
                label="Your Email"
                variant="outlined"
                value={email ?? ""}
                onChange={(e) => setEmail(e.currentTarget.value)}
                size="small"
                disabled={!!signInRequestResponse}
              />
              {!signInRequestResponse && (
                <FormControlLabel
                  control={
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        size="small"
                        checked={staySignedInDays > 1}
                        onChange={(e) => {
                          setStaySignedInDays(e.target.checked ? 7 : 1);
                        }}
                      />
                      <Typography variant="body2" mr={1}>
                        Stay signed in for
                      </Typography>
                      <Select
                        value={staySignedInDays}
                        size="small"
                        disabled={staySignedInDays === 1}
                        onChange={(e) =>
                          setStaySignedInDays(Number(e.target.value))
                        }
                        sx={{ minWidth: 70 }}
                      >
                        <MenuItem value={1}>1 day</MenuItem>
                        <MenuItem value={7}>7 days</MenuItem>
                        <MenuItem value={30}>30 days</MenuItem>
                        <MenuItem value={90}>90 days</MenuItem>
                      </Select>
                    </Box>
                  }
                  label=""
                />
              )}
              <Button
                variant="contained"
                onClick={() => handleSignInClick()}
                disabled={
                  !!signInRequestResponse ||
                  !email ||
                  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
                }
              >
                Sign-in via email
              </Button>
              {errorMessage && !signInRequestResponse && (
                <Typography variant="body2" color="error">
                  {errorMessage}
                </Typography>
              )}
              <Typography variant="body2">
                {!signInRequestResponse
                  ? `We'll email you a magic code for a password free sign-in. Email
                addresses from ${enterpriseData?.domainWhitelist || ""} are
                allowed.`
                  : `An one-time-code valid until ${new Date(
                      signInRequestResponse.OTCExpires
                    ).toLocaleTimeString()} was emailed to you. Please enter it below.`}
              </Typography>

              {signInRequestResponse && renderOtpInput()}

              {enterpriseData?.policy && (
                <>
                  <Typography variant="body2">
                    By logging in you agree to policies and acceptable use:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ whiteSpace: "pre-wrap", marginTop: 1 }}
                  >
                    {enterpriseData?.policy}
                  </Typography>
                </>
              )}
            </Stack>
          </Box>
        </Box>
      )}

      {token && (
        <Box
          padding={0}
          margin={0}
          width="100%"
          height="100vh"
          sx={{ overflow: "hidden" }}
        >
          <AppBar
            position="static"
            color="default"
            elevation={1}
            sx={{ bgcolor: "background.paper" }}
          >
            <Toolbar variant="dense">
              <Tooltip title="Return to home">
                <IconButton onClick={() => setConversation(null)} size="small">
                  <HomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {` LLM Chat Service for ${
                  enterpriseData?.name ?? ""
                }. You are logged in as ${customerId}.`}
              </Typography>
              <Button variant="text" size="small" onClick={handleSignout}>
                Sign-out
              </Button>
            </Toolbar>
          </AppBar>

          <Box display="flex" width="100%" height="calc(100vh - 48px)">
            <Box
              width="250px"
              minWidth="250px"
              maxWidth="250px"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                padding: 1,
                boxSizing: "border-box",
                flexShrink: 0,
              }}
            >
              <Paper
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Button variant="contained" onClick={handleMenuOpen} fullWidth>
                  Start new chat
                </Button>

                {/* debug button for token refresh 
                <Button
                  variant="text"
                  onClick={() => {
                    const email = getCookie(COOKIE_EMAIL);
                    const token = getCookie(COOKIE_TOKEN);
                    doRefreshToken(email ?? "", token ?? "");
                  }}
                >
                  Refresh Token
                </Button>
                */}

                <Menu
                  id="agentMenu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem key="" onClick={() => handleAgentSelect(null)}>
                    <em>Blank chat (no agent)</em>
                  </MenuItem>
                  {agents.map((entry) => (
                    <MenuItem
                      key={entry.id}
                      onClick={() => handleAgentSelect(entry)}
                    >
                      {entry.name}
                    </MenuItem>
                  ))}
                </Menu>

                <List
                  dense
                  className={classes.listContainer}
                  sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                    flexGrow: 1,
                    overflow: "auto",
                    marginBottom: "50px", // for deleted and theme controls
                  }}
                >
                  {(() => {
                    // Group conversations by date
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const oneWeekAgo = new Date(today);
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

                    // Fix the date comparisons by ensuring we're comparing properly
                    const todayConversations = conversationList.filter((c) => {
                      const createdDate = new Date(c.createdAt);
                      return (
                        (showDeletedConversations || c.status === "active") &&
                        createdDate >= today
                      );
                    });

                    const lastWeekConversations = conversationList.filter(
                      (c) => {
                        const createdDate = new Date(c.createdAt);
                        return (
                          (showDeletedConversations || c.status === "active") &&
                          createdDate < today &&
                          createdDate >= oneWeekAgo
                        );
                      }
                    );

                    const olderConversations = conversationList.filter((c) => {
                      const createdDate = new Date(c.createdAt);
                      return (
                        (showDeletedConversations || c.status === "active") &&
                        createdDate < oneWeekAgo
                      );
                    });

                    // If no conversations in any group, show all conversations without grouping
                    if (
                      todayConversations.length === 0 &&
                      lastWeekConversations.length === 0 &&
                      olderConversations.length === 0 &&
                      conversationList.length > 0
                    ) {
                      return conversationList
                        .filter(
                          (c) =>
                            showDeletedConversations || c.status === "active"
                        )
                        .map(renderConversationItem);
                    }

                    // Render the grouped conversations
                    return (
                      <>
                        {todayConversations.length > 0 && (
                          <>
                            <ListItem
                              component="div" // Make sure it's rendered
                              disableGutters={false}
                              sx={{
                                py: 0.5,
                                px: 1,
                                backgroundColor: "rgba(25, 118, 210, 0.12)",
                                fontWeight: "bold",
                                borderRadius: "4px",
                                marginTop: 1,
                                marginBottom: 0.5,
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                Today
                              </Typography>
                            </ListItem>
                            {todayConversations.map(renderConversationItem)}
                          </>
                        )}

                        {lastWeekConversations.length > 0 && (
                          <>
                            <ListItem
                              component="div"
                              disableGutters={false}
                              sx={{
                                py: 0.5,
                                px: 1,
                                backgroundColor: "rgba(25, 118, 210, 0.12)",
                                fontWeight: "bold",
                                borderRadius: "4px",
                                marginTop: 1,
                                marginBottom: 0.5,
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                This Week
                              </Typography>
                            </ListItem>
                            {lastWeekConversations.map(renderConversationItem)}
                          </>
                        )}

                        {olderConversations.length > 0 && (
                          <>
                            <ListItem
                              component="div"
                              disableGutters={false}
                              sx={{
                                py: 0.5,
                                px: 1,
                                backgroundColor: "rgba(25, 118, 210, 0.12)",
                                fontWeight: "bold",
                                borderRadius: "4px",
                                marginTop: 1,
                                marginBottom: 0.5,
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                Older
                              </Typography>
                            </ListItem>
                            {olderConversations.map(renderConversationItem)}
                          </>
                        )}

                        {/* Fallback if no conversations match the filtering criteria */}
                        {conversationList.length > 0 &&
                          todayConversations.length === 0 &&
                          lastWeekConversations.length === 0 &&
                          olderConversations.length === 0 &&
                          conversationList.map(renderConversationItem)}
                      </>
                    );
                  })()}
                </List>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                    p: 1,
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "background.paper",
                    zIndex: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showDeletedConversations}
                        onChange={(e) =>
                          setShowDeletedConversations(e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography variant="caption">Show deleted</Typography>
                    }
                  />
                  <Tooltip
                    title={
                      getTheme() === "dark"
                        ? "Switch to light theme"
                        : "Switch to dark theme"
                    }
                  >
                    <IconButton onClick={toggleTheme} size="small">
                      {getTheme() === "dark" ? (
                        <LightModeIcon fontSize="small" />
                      ) : (
                        <DarkModeIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            </Box>
            {conversation ? (
              <Box
                flexGrow={1}
                height="100%"
                padding={1}
                sx={{
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  boxSizing: "border-box",
                }}
              >
                {isConversationLoading && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bgcolor="rgba(255,255,255,0.7)"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={10}
                  >
                    <CircularProgress size={40} sx={{ marginBottom: 2 }} />
                    <Typography variant="h6">
                      Loading conversation...
                    </Typography>
                  </Box>
                )}

                {selectedAgent ? (
                  <AgentPanel
                    agent={selectedAgent.id}
                    url={url}
                    theme={getTheme() === "dark" ? "dark" : "light"}
                    conversation={conversation ? conversation.id : null}
                    customer={{
                      customer_id: email ?? "",
                      customer_name: email ?? "",
                    }}
                    initialHistory={conversation ? conversation.history : {}}
                    height="100%"
                    width="100%"
                  />
                ) : (
                  <ChatPanel
                    project_id={id ?? ""}
                    conversation={conversation ? conversation.id : null}
                    url={url}
                    initialMessage={`Hi. This is a managed LLM generative AI chat service. Please remember to follow the guidelines for using this service.`}
                    theme={getTheme() === "dark" ? "dark" : "light"}
                    height="100%"
                    width="100%"
                    hideInitialPrompt={false}
                    title=""
                    agent={null}
                    customer={{
                      customer_id: email ?? "",
                      customer_name: email ?? "",
                    }}
                    initialHistory={conversation ? conversation.history : {}}
                    customerEmailCaptureMode="HIDE"
                  />
                )}
              </Box>
            ) : (
              <Box
                flexGrow={1}
                height="100%"
                padding={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(3, 1fr)"
                  gap={3}
                  width="100%"
                  maxWidth="900px"
                >
                  {/* Blank Chat Option */}
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      height: "180px",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "action.hover",
                        transform: "translateY(-4px)",
                        transition: "transform 0.2s",
                      },
                    }}
                    onClick={() => handleAgentSelect(null)}
                  >
                    <Typography variant="h6">Blank Chat</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      Start a conversation without any specific agent. General
                      purpose chat.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        alignSelf: "flex-start",
                        mt: "auto",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Start Chat
                    </Button>
                  </Paper>

                  {/* Agent Options */}
                  {agents.map((agent) => (
                    <Paper
                      key={agent.id}
                      elevation={2}
                      sx={{
                        p: 2,
                        height: "180px",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "action.hover",
                          transform: "translateY(-4px)",
                          transition: "transform 0.2s",
                        },
                      }}
                      onClick={() => handleAgentSelect(agent)}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          flexGrow: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {agent.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          flexGrow: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {agent.description || "No description available"}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          mt: "auto",
                          whiteSpace: "nowrap",
                        }}
                        color="primary"
                      >
                        Start Chat
                      </Button>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Menu
        id="conversationMenu"
        anchorEl={conversationMenuAnchorEl}
        open={Boolean(conversationMenuAnchorEl)}
        onClose={handleConversationMenuClose}
      >
        <MenuItem onClick={handleRenameConversation}>Rename</MenuItem>
        <MenuItem onClick={handleDeleteConversation}>
          {menuConversation?.status === "active" ? "Delete" : "Un-delete"}
        </MenuItem>
      </Menu>

      <Modal open={renameOpen} onClose={handleRenameClose}>
        <Box
          sx={{
            position: "absolute",
            top: modalPosition.top,
            left: modalPosition.left,
            bgcolor: "background.paper",
            boxShadow: 4,
            p: 2,
            minWidth: "250px",
            borderRadius: 1,
          }}
        >
          <TextField
            label="Conversation Name"
            size="small"
            value={newConversationName}
            onChange={(e) => setNewConversationName(e.target.value)}
          />
          <Button variant="contained" onClick={handleRenameSubmit}>
            Rename
          </Button>
          <br />
          <Button
            variant="text"
            onClick={async () => {
              if (menuConversation) {
                setIsSuggesting(true);

                // if they are renaming a conversation NOT yet loaded, load it
                if (Object.keys(menuConversation.history).length === 0) {
                  await menuConversation.load(token);
                }

                const historyLength =
                  Object.keys(menuConversation.history).length ?? 0;

                console.log("History length: ", historyLength);

                if (historyLength > 0) {
                  let prompt =
                    "Based on this conversation, suggest a brief title (3-4 words max) with no other formatting or preamble:\n\n.";

                  const firstEntry = Object.entries(
                    menuConversation.history
                  )[0];

                  prompt += "Prompt: " + firstEntry[0] + "\n";
                  prompt += "Response: " + firstEntry[1].content + "\n\n";

                  if (historyLength > 1) {
                    const secondEntry = Object.entries(
                      menuConversation.history
                    )[1];

                    prompt += "Prompt: " + secondEntry[0] + "\n";
                    prompt += "Response: " + secondEntry[1].content + "\n\n";
                  }

                  if (prompt.length > 0) {
                    const suggest = (await send(
                      prompt,
                      [],
                      [],
                      false
                    )) as string;
                    if (suggest && suggest.length > 0) {
                      setNewConversationName(
                        suggest
                          .replace('"', "")
                          .replace("(PII redacted)", "")
                          .replace("(cached)", "")
                      );

                      setIsSuggesting(false);
                    }
                  }
                }
              }
            }}
          >
            Suggest
          </Button>
          <CircularProgress
            size={20}
            sx={{ visibility: isSuggesting ? "visible" : "hidden" }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default EnterpriseChat;
